import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KT, useKidsDarkMode } from "@/context/KidsDarkModeContext";
import { gradeLevelStorage } from "@/lib/localStorage";
import {
  dismissKidsAlert,
  installKidsToastBridge,
  subscribeToKidsAlerts,
  type KidsAlert,
  type KidsAlertKind,
} from "@/lib/kidsAlerts";

// Kids palette: the same greens and blues as the rest of Kids Mode.
const LOOK: Record<KidsAlertKind, { color: string; tint: string; Icon: typeof Info; heading: string }> = {
  success: { color: "#58CC02", tint: "rgba(88,204,2,0.14)", Icon: CheckCircle2, heading: "Well done!" },
  info: { color: "#1CB0F6", tint: "rgba(28,176,246,0.14)", Icon: Info, heading: "Just so you know" },
  warning: { color: "#FF9600", tint: "rgba(255,150,0,0.16)", Icon: AlertTriangle, heading: "Hold on a moment" },
  error: { color: "#FF4B4B", tint: "rgba(255,75,75,0.14)", Icon: AlertTriangle, heading: "Oops!" },
};

/**
 * Shows alerts for a kids account as a modal, one at a time. Renders nothing
 * and changes nothing for adult accounts.
 */
export default function KidsAlertHost() {
  const { isDark } = useKidsDarkMode();
  const [queue, setQueue] = useState<readonly KidsAlert[]>([]);
  const isKids = gradeLevelStorage.isKids();

  useEffect(() => {
    if (!isKids) return;
    const restore = installKidsToastBridge(toast as unknown as Record<string, unknown>);
    const unsubscribe = subscribeToKidsAlerts(setQueue);
    return () => {
      unsubscribe();
      restore();
    };
  }, [isKids]);

  const current = queue[0];
  if (!isKids || !current) return null;
  const look = LOOK[current.kind];
  const close = () => dismissKidsAlert(current.id);

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        className="max-w-sm p-6 sm:p-7"
        style={{ background: KT.card(isDark), borderColor: isDark ? "rgba(255,255,255,0.14)" : look.color }}
      >
        <DialogHeader className="items-center text-center">
          <div className="mb-1 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: look.tint }}>
            <look.Icon className="h-8 w-8" style={{ color: look.color }} aria-hidden="true" />
          </div>
          <p className="text-xs font-black uppercase tracking-wide" style={{ color: look.color }}>
            {look.heading}
          </p>
          <DialogTitle className="font-sans text-xl font-black leading-snug" style={{ color: KT.heading(isDark) }}>
            {current.title}
          </DialogTitle>
          {current.description ? (
            <DialogDescription className="text-center text-base font-semibold leading-relaxed" style={{ color: KT.body(isDark) }}>
              {current.description}
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{current.title}</DialogDescription>
          )}
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-3">
          {current.actionLabel && current.onAction && (
            <button
              type="button"
              onClick={() => {
                current.onAction?.();
                close();
              }}
              className="min-h-12 rounded-2xl border-2 px-5 text-base font-black"
              style={{ borderColor: look.color, color: look.color, background: "transparent" }}
            >
              {current.actionLabel}
            </button>
          )}
          <button
            type="button"
            autoFocus
            onClick={close}
            className="min-h-12 rounded-2xl px-5 text-base font-black text-white"
            style={{ background: look.color, boxShadow: `0 4px 0 ${isDark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.18)"}` }}
          >
            Got it
          </button>
          {queue.length > 1 && (
            <p className="text-center text-xs font-bold" style={{ color: KT.sub(isDark) }}>
              {queue.length - 1} more to read
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
